// janus-engine/combat/opposition.ts — R-119 (Janus lane N5, 2026-10-01): "no opposition left" decided in the browser.
//
// The port of ft_auto_exit_combat_if_last_combatant_standing() + ft_combat_has_opposition() (was exit_encounter_combat.php /
// combat_membership.php). Owner rules (2026-07-19, 2026-08-26): after a death or a removal, combat ends once no two LIVING members stand
// on opposing teams — a `friend` and an `enemy` (lairs never count; a lone survivor, a party with every foe down, or a lair whose boss
// died all end it). An untagged legacy combat (no active combat id) ends when the initiative order holds at most one combatant.
// EDGE-triggered, like the PHP it replaces (the old queue ran only when a removal happened): a fight that never had two sides does not end
// itself — only a living member LEAVING (dead, deleted, gone from the roster, untagged) arms the check. Pure: rows in, a verdict out.
import { initiativeOrder } from '../turns/end-turn.js';

type Row = Record<string, unknown>;
const asRow = (v: unknown): Row | null => v !== null && typeof v === 'object' && !Array.isArray(v) ? v as Row : null;
const scalar = (v: unknown): unknown => (Array.isArray(v) ? v[0] : v);
const upper = (v: unknown): string => String(scalar(v) ?? '').trim().toUpperCase();
const int = (v: unknown): number => { const n = Number(scalar(v) ?? 0); return Number.isFinite(n) ? Math.trunc(n) : 0; };

/** TeamType (includes/additions.php). */
export const TEAM_FRIEND = '7DBEB6D4-D147-703A-022E-05B290BB95C3';
export const TEAM_ENEMY = '9399856B-2132-7574-1684-91139A2831DD';

const ecOf = (row: Row): Row => asRow(row.encounter_creatures) ?? {};
const idOf = (row: Row): string => upper(ecOf(row).encounter_creature_id);
const itemType = (row: Row): string => String(scalar(ecOf(row).item_type) ?? scalar(asRow(row.items)?.item_type) ?? '').toLowerCase();
function rosterOf(encounter: Row | null): Row[] {
  return Array.isArray(encounter?.encounter_creatures_arr) ? (encounter!.encounter_creatures_arr as unknown[]).map(asRow).filter((r): r is Row => !!r) : [];
}
function field(encounter: Row | null, key: string): unknown {
  const bucket = asRow(encounter?.encounters);
  return scalar(bucket && key in bucket ? bucket[key] : encounter?.[key]);
}
/** The team id of a row (a bare id or an {items:{item_id}} shell), '' when the page holds none. */
export function teamOf(row: Row): string {
  const raw = scalar(ecOf(row).team);
  return upper(asRow(raw)?.items ? asRow(asRow(raw)!.items)?.item_id : raw);
}

export interface CombatSides {
  /** null = the page cannot read the encounter's combat flag. */
  inCombat: boolean | null;
  combatId: string;
  /** Living members of the fight (tagged on the active combat; untagged legacy: the initiative order), lairs included. */
  living: string[];
  /** true when two living non-lair members stand on opposing teams (untagged legacy: more than one in the initiative order). */
  opposition: boolean;
  /** Living non-lair members whose team the page cannot read — the verdict is then unknown, never "no opposition". */
  unknownTeam: string[];
  /** Set when the page cannot judge membership at all: no roster held (a reload / a partial snapshot) or a partly tagged legacy roster.
   *  Uncertainty is never encoded as an empty fight (Codex review 2026-10-01). */
  unknown?: 'roster' | 'membership';
}

/** The fight as the page holds it. */
export function combatSides(encounter: Row | null): CombatSides {
  const flag = field(encounter, 'in_combat');
  const inCombat = !encounter ? null : (flag === undefined || flag === null || flag === '') ? (asRow(encounter.encounters) ? false : null) : int(flag) === 1;
  const combatId = upper(field(encounter, 'active_combat_id'));
  const roster = rosterOf(encounter);
  if (!inCombat) return { inCombat, combatId, living: [], opposition: false, unknownTeam: [] };
  if (!Array.isArray(encounter!.encounter_creatures_arr) || roster.length === 0) return { inCombat, combatId, living: [], opposition: true, unknownTeam: [], unknown: 'roster' };
  if (!combatId) {
    // The untagged legacy combat: build_initiative_encounter_creature_id_arr() count, every resident a member. A partly tagged roster
    // is incoherent (an active id still arriving): unknown, never an empty fight.
    if (roster.some(r => upper(ecOf(r).combat_id))) return { inCombat, combatId, living: [], opposition: true, unknownTeam: [], unknown: 'membership' };
    const order = initiativeOrder(encounter!, { roster: () => roster, inCombat: () => true }).order;
    return { inCombat, combatId, living: order, opposition: order.length > 1, unknownTeam: [] };
  }
  const living: string[] = [];
  const unknownTeam: string[] = [];
  let friend = false, enemy = false;
  for (const row of roster) {
    const ec = ecOf(row);
    if (upper(ec.combat_id) !== combatId || int(ec.encounter_creature_deleted) !== 0 || int(ec.encounter_creature_dead) !== 0) continue;
    const id = idOf(row);
    if (!id) continue;
    living.push(id);
    if (itemType(row) === 'lair') continue;
    const team = teamOf(row);
    if (!team) { unknownTeam.push(id); continue; }
    if (team === TEAM_FRIEND) friend = true;
    if (team === TEAM_ENEMY) enemy = true;
  }
  return { inCombat, combatId, living, opposition: friend && enemy, unknownTeam };
}

export interface FightMemory { combatId: string; living: string[] }
export type AutoExitVerdict =
  | { exit: true; combatId: string; removed: string[] }
  | { exit: false; why: 'out-of-combat' | 'combat-unknown' | 'roster-unknown' | 'first-sight' | 'new-combat' | 'no-removal' | 'opposition' | 'team-unknown' };

/**
 * One evaluation against what the page saw last time (`prev`, per encounter). Returns the verdict and the memory to keep. A removal is
 * an id that WAS a living member of the same combat and no longer is — additions never hide it (set difference, not a count).
 */
export function autoExitVerdict(prev: FightMemory | null, encounter: Row | null): { verdict: AutoExitVerdict; memory: FightMemory | null } {
  const sides = combatSides(encounter);
  if (sides.inCombat === null) return { verdict: { exit: false, why: 'combat-unknown' }, memory: prev };
  if (!sides.inCombat) return { verdict: { exit: false, why: 'out-of-combat' }, memory: null };
  // A page that cannot read the membership keeps what it last knew — neither a removal nor a baseline comes from a snapshot it cannot judge.
  if (sides.unknown) return { verdict: { exit: false, why: 'roster-unknown' }, memory: prev };
  const memory: FightMemory = { combatId: sides.combatId, living: sides.living };
  if (!prev) return { verdict: { exit: false, why: 'first-sight' }, memory };
  if (prev.combatId !== sides.combatId) return { verdict: { exit: false, why: 'new-combat' }, memory };
  const now = new Set(sides.living);
  const removed = prev.living.filter(id => !now.has(id));
  if (!removed.length) return { verdict: { exit: false, why: 'no-removal' }, memory };
  // An unreadable team keeps the OLD baseline, so the removal is judged again once the team arrives (it is not consumed here).
  if (sides.unknownTeam.length) return { verdict: { exit: false, why: 'team-unknown' }, memory: prev };
  if (sides.opposition) return { verdict: { exit: false, why: 'opposition' }, memory };
  return { verdict: { exit: true, combatId: sides.combatId, removed }, memory };
}
